'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import Cta from "@/components/cta/cta";
import Close from "@/components/icon/close";
import { useApp } from '@/context/AppContext';

export default function RecordTranscript({ className = '', paragraphs, onClose }) {
  const dialogRef = useRef(null);
  const backgroundRef = useRef(null);
  const contentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleRef = useRef(null);
  const textRef = useRef(null);
  const previousActiveElement = useRef(null);
  const onCloseRef = useRef(onClose);
  const { isInitialLoad } = useApp()

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  /** Animates the transcript entering */
  const enter = () => {
    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    tl.addLabel('start');

    tl.fromTo(backgroundRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 }, 'start');
    tl.fromTo(contentRef.current, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.5, clearProps: 'transform' }, 'start');
    tl.fromTo(closeButtonRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4 });
    tl.fromTo(titleRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start+=0.05');
    tl.fromTo(textRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start+=0.07');

    return tl;
  };

  /** Animates the transcript leaving and calls onClose when complete */
  const leave = () => {
    const tl = gsap.timeline({
      defaults: { ease: 'power2.in' },
      onComplete: () => onCloseRef.current(),
    });

    tl.addLabel('start');

    tl.to(textRef.current, { opacity: 0, duration: 0.3 }, 'start');
    tl.to(titleRef.current, { opacity: 0, duration: 0.3 }, 'start');
    tl.to(closeButtonRef.current, { opacity: 0, duration: 0.3 }, 'start');
    tl.to(contentRef.current, { opacity: 0, duration: 0.4 }, 'start');
    tl.to(backgroundRef.current, { opacity: 0, duration: 0.4 }, 'start');

    return tl;
  };

  useGSAP(() => {
    // Set initial states
    gsap.set([
      backgroundRef.current,
      contentRef.current,
      closeButtonRef.current,
      titleRef.current,
      textRef.current,
    ], { opacity: 0 });

    // Play enter animation
    enter();
  }, []);

  useEffect(() => {
    previousActiveElement.current = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        leave();
        return;
      }

      // Focus trap: cycle focus within dialog
      if (e.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements?.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, []);

  useEffect(() => {
    gsap.set(dialogRef.current, {
      y: isInitialLoad ? window.scrollY : 0
    })
  }, [])

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="transcript-title"
      data-lenis-prevent
      className={`fixed inset-0 top-0 left-0 z-22 overflow-y-scroll h-screen overscroll-contain scroll-smooth ${className}`}
    >
      <div ref={backgroundRef} className="fixed inset-0 bg-story-theme -z-1" aria-hidden="true" />

      <div className="grid-9">
        <div ref={contentRef} className="min-h-[calc(100svh-var(--outer-gutter-alt)*2)] col-span-full px-7 my-(--outer-gutter-alt) bg-white rounded-2xl lg:rounded-3xl lg:my-23">
          <Cta
            ref={closeButtonRef}
            className="absolute z-2 top-[calc(var(--outer-gutter-alt)+16px)] right-[calc(var(--outer-gutter-alt)+16px)] cta-light cta-icon-stroke-theme cta-circle-small lg:top-30 lg:right-[calc(var(--outer-gutter-alt)+28px)] lg:cta-circle"
            aria-label="Close transcript"
            onClick={leave}
          >
            <Close className="w-(--space-7) h-auto" aria-hidden="true" />
          </Cta>

          <h2 ref={titleRef} id="transcript-title" className="mt-9 heading-8 text-main-dark opacity-60 lg:text-center lg:mt-10.5">Audio Transcript</h2>
          <div ref={textRef} className="flex flex-col gap-y-6 mt-6.5 text-transcript-small pb-27 lg:mt-9 lg:mx-42 lg:gap-y-13 lg:text-transcript" dangerouslySetInnerHTML={{__html: paragraphs}}>
          </div>
        </div>
      </div>
    </div>
  )
}
